// Stations tab (spec/13 §12, F-ORG-03 rules 3-4, #408): the PCs, consoles and other stations at a
// venue, as a list to scan and search — or as cards. Adding one is a short Stepper in a centred dialog
// (Type → Details → Pair, `AddStationDialog`), and pairing a PC to a station opens its own small dialog
// (F-STN-02), so the list is never pushed down by a form. "Download apps" opens the two installers a PC is set up with —
// Clavian Station and Clavian Counter (F-STN-17) — as plain downloads, in a dialog too.
//
// `stations:read` is needed to see it and `stations:manage` to add or pair: a member without manage
// sees the list read-only, and the server checks both again on every request (spec/13 §1).

import type { Station, StationClass, StationClient, StationKind } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Chip, Dialog, EmptyState, Menu, StatusPill, Table, type MenuEntry, type TableColumn } from '@clavian/ui-web';
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { LoadFailure } from '../shell/LoadFailure';
import { Icon, NavIcon } from '../shell/nav-icons';
import { CardsSkeleton, Forbidden, SearchField, TableSkeleton, Toolbar, ToolbarSpring, ViewToggle, type ViewMode } from '../ui';
import { AddStationDialog } from './AddStationDialog';
import { ClientCell } from './client';
import { EditStationDialog } from './EditStationDialog';
import { InstallerDownloads } from './InstallerDownloads';
import { tokenColor } from './fields';
import { pairingOf } from './pairing';
import { PairingForm } from './PairingForm';
import { useStationClasses, useStationClients, useStations, useZones } from './queries';
import { StationRemovalDialog, type RemovalTarget } from './StationRemoval';

export function StationsTab({
  organizationId,
  venueId,
  canManage,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly canManage: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const stations = useStations(organizationId, venueId);
  const classes = useStationClasses(organizationId, venueId);
  const zones = useZones(organizationId, venueId);
  // Each PC's station client (F-STN-12): one request for the venue, joined here by station id.
  const clients = useStationClients(organizationId, venueId);
  const [query, setQuery] = useState('');
  const [classFilter, setClassFilter] = useState<string | null>(null);
  // Cards on a phone, where a table's columns would scroll sideways; the table on a desk.
  const [view, setView] = useState<ViewMode>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 40rem)').matches ? 'grid' : 'list',
  );
  const [adding, setAdding] = useState(false);
  const [pairing, setPairing] = useState<Station | null>(null);
  const [editing, setEditing] = useState<Station | null>(null);
  const [downloads, setDownloads] = useState(false);
  const [removal, setRemoval] = useState<RemovalTarget | null>(null);

  const classById = useMemo(() => new Map((classes.data ?? []).map((klass) => [klass.id, klass])), [classes.data]);
  const clientOf = useMemo(() => new Map<string, StationClient>((clients.data?.data ?? []).map((client) => [client.stationId, client])), [clients.data]);
  const zoneNameOf = useMemo(() => new Map((zones.data ?? []).map((zone) => [zone.id, zone.name])), [zones.data]);
  const rows = useMemo(() => stations.data?.pages.flatMap((page) => page.data) ?? [], [stations.data]);

  const needle = query.trim().toLowerCase();
  const shown = rows.filter((station) => {
    if (classFilter !== null && station.classId !== classFilter) return false;
    if (needle === '') return true;
    return String(station.number).includes(needle) || (station.name ?? '').toLowerCase().includes(needle) || t(`stations.kind.${station.kind}`).toLowerCase().includes(needle);
  });

  const columns: readonly TableColumn<Station>[] = [
    {
      key: 'number',
      header: t('stations.column.number'),
      cell: (station) => (
        <span className="console-station-cell">
          {/* The number as the floor's key cap; the name beside it only when it has one ("16" is not also "Station 16"). */}
          <span className="console-keycap console-station-cell__number">{station.number}</span>
          <span className="console-station-cell__text">
            {station.name === null ? null : <span className="console-station-cell__name">{station.name}</span>}
            <RoleBadge station={station} />
            {station.status === 'disabled' ? (
              <StatusPill state="offline" size="sm">
                {t('stations.status.disabled')}
              </StatusPill>
            ) : null}
          </span>
        </span>
      ),
    },
    { key: 'kind', header: t('stations.column.kind'), cell: (station) => <KindLabel kind={station.kind} /> },
    { key: 'class', header: t('stations.column.class'), cell: (station) => <ClassLabel klass={classById.get(station.classId)} fallback={station.classId} /> },
    {
      key: 'zone',
      header: t('stations.column.zone'),
      cell: (station) => (station.zoneId === null ? <span className="console-subtle">{t('stations.noZone')}</span> : (zoneNameOf.get(station.zoneId) ?? station.zoneId)),
    },
    { key: 'capacity', header: t('stations.column.capacity'), numeric: true, cell: (station) => <span className="console-num">{station.capacity}</span> },
    { key: 'pairing', header: t('stations.column.pairing'), cell: (station) => <PairingCell station={station} canManage={canManage} onPair={() => setPairing(station)} /> },
    { key: 'client', header: t('stations.column.client'), cell: (station) => <ClientCell client={clientOf.get(station.id)} /> },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: t('stations.column.actions'),
            cell: (station: Station) => (
              <span className="console-station-row-actions">
                <Button variant="ghost" size="sm" icon={<Icon name="pencil" />} onClick={() => setEditing(station)} label={t('stations.edit.actionNamed', { number: station.number })}>
                  {t('stations.edit.action')}
                </Button>
                <StationMenu station={station} onRemoval={(kind) => setRemoval(removalOf(station, kind))} />
              </span>
            ),
          } satisfies TableColumn<Station>,
        ]
      : []),
  ];

  let list: ReactNode;
  if (stations.data === undefined) {
    if (stations.isError && problemCode(stations.error) === PROBLEM.forbidden) {
      list = <Forbidden message={t('stations.notPermitted')} />;
    } else if (stations.isError) {
      list = <LoadFailure error={stations.error} message={t('stations.unavailable')} onRetry={() => void stations.refetch()} />;
    } else {
      list = view === 'grid' ? <CardsSkeleton /> : <TableSkeleton />;
    }
  } else if (rows.length === 0) {
    list = (
      <EmptyState
        icon={<NavIcon name="stations" />}
        title={t('stations.emptyTitle')}
        description={t('stations.empty')}
        action={
          canManage ? (
            <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setAdding(true)}>
              {t('stations.add.title')}
            </Button>
          ) : undefined
        }
      />
    );
  } else if (view === 'grid') {
    list =
      shown.length === 0 ? (
        <EmptyState size="compact" title={t('common.noMatches', { query: query.trim() })} />
      ) : (
        <ul className="console-station-cards" aria-label={t('stations.caption')}>
          {shown.map((station) => (
            <li key={station.id}>
              <StationCard station={station} client={clientOf.get(station.id)} klass={classById.get(station.classId)} zoneName={station.zoneId === null ? null : (zoneNameOf.get(station.zoneId) ?? null)} canManage={canManage} onPair={() => setPairing(station)} onEdit={() => setEditing(station)} onRemoval={(kind) => setRemoval(removalOf(station, kind))} />
            </li>
          ))}
        </ul>
      );
  } else {
    list = (
      <div className="console-table-card">
        <Table
          caption={t('stations.caption')}
          captionHidden
          columns={columns}
          rows={shown}
          rowKey={(station) => station.id}
          rowTone={(station) => stationTone(station)}
          empty={t('common.noMatches', { query: query.trim() })}
        />
        {stations.hasNextPage ? (
          <div className="console-list-foot">
            <Button variant="ghost" busy={stations.isFetchingNextPage} onClick={() => void stations.fetchNextPage()}>
              {t('stations.more')}
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="console-tabpane">
      <Toolbar>
        <SearchField label={t('stations.search')} value={query} onChange={(event) => setQuery(event.target.value)} />
        {(classes.data ?? []).length > 1 ? (
          <div className="console-chips" role="group" aria-label={t('stations.filterByClass')}>
            {(classes.data ?? []).map((klass) => (
              <Chip
                key={klass.id}
                selected={classFilter === klass.id}
                onSelectedChange={(selected) => setClassFilter(selected ? klass.id : null)}
                count={rows.filter((station) => station.classId === klass.id).length}
                icon={<span className="console-chip-dot" style={{ backgroundColor: tokenColor(klass.colorToken) }} />}
              >
                {klass.name}
              </Chip>
            ))}
          </div>
        ) : null}
        <ToolbarSpring />
        <ViewToggle value={view} onValueChange={setView} />
        <Button variant="secondary" icon={<Icon name="download" />} onClick={() => setDownloads(true)}>
          {t('stations.installers.action')}
        </Button>
        {/* With no station yet the empty state offers Add station; the toolbar does not offer it a second time. */}
        {canManage && rows.length > 0 ? (
          <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setAdding(true)}>
            {t('stations.add.title')}
          </Button>
        ) : null}
      </Toolbar>
      {list}
      {canManage ? <AddStationDialog organizationId={organizationId} venueId={venueId} open={adding} onOpenChange={setAdding} /> : null}
      <PairDialog station={pairing} onClose={() => setPairing(null)} />
      <EditStationDialog station={editing} onClose={() => setEditing(null)} />
      <StationRemovalDialog target={removal} onClose={() => setRemoval(null)} />
      <Dialog open={downloads} onOpenChange={setDownloads} size="md" title={t('stations.installers.sheetTitle')} description={t('stations.installers.sheetHint')}>
        {downloads ? <InstallerDownloads organizationId={organizationId} venueId={venueId} /> : null}
      </Dialog>
    </div>
  );
}

/**
 * A station's state on the list, as the floor tile's language (ADR-0043): a PC waiting to be paired wants a look (warn);
 * one taken out of use is quiet in the table and hatched as a card; a working one keeps its class's colour.
 */
function stationTone(station: Station): 'warn' | 'quiet' | undefined {
  if (station.status === 'disabled') return 'quiet';
  if (station.managed !== false && pairingOf(station).status !== 'paired') return 'warn';
  return undefined;
}

/** "Counter" on the venue's Clavian Counter PC (F-STN-17): a device of the venue, not a PC on the floor. */
function RoleBadge({ station }: { readonly station: Station }): ReactNode {
  const { t } = useTranslation('console');
  if (station.role !== 'counter') return null;
  return (
    <span title={t('stations.role.counterHint')}>
      <Badge tone="neutral">{t('stations.role.counter')}</Badge>
    </span>
  );
}

function KindLabel({ kind }: { readonly kind: StationKind }): ReactNode {
  const { t } = useTranslation('console');
  return <span className="console-muted">{t(`stations.kind.${kind}`)}</span>;
}

function ClassLabel({ klass, fallback }: { readonly klass: StationClass | undefined; readonly fallback: string }): ReactNode {
  return (
    <span className="console-class-label">
      <span className="console-chip-dot" style={klass === undefined ? undefined : { backgroundColor: tokenColor(klass.colorToken) }} aria-hidden="true" />
      {klass?.name ?? fallback}
    </span>
  );
}

function StationCard({
  station,
  client,
  klass,
  zoneName,
  canManage,
  onPair,
  onEdit,
  onRemoval,
}: {
  readonly station: Station;
  readonly client: StationClient | undefined;
  readonly klass: StationClass | undefined;
  readonly zoneName: string | null;
  readonly canManage: boolean;
  readonly onPair: () => void;
  readonly onEdit: () => void;
  readonly onRemoval: (kind: RemovalTarget['kind']) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <article
      className="console-station-card console-rail"
      data-rail={station.status === 'disabled' ? 'off' : stationTone(station) === 'warn' ? 'warn' : 'class'}
      style={klass === undefined ? undefined : ({ '--tile-class': tokenColor(klass.colorToken), '--console-rail-class': tokenColor(klass.colorToken) } as CSSProperties)}
    >
      <header className="console-station-card__head">
        <span className="console-station-card__number console-figure">{station.number}</span>
        <PairingCell station={station} canManage={canManage} onPair={onPair} />
      </header>
      <h3 className="console-station-card__name">
        {station.name ?? t('floor.tile.numbered', { number: station.number })}
        <RoleBadge station={station} />
        {station.status === 'disabled' ? (
          <StatusPill state="offline" size="sm">
            {t('stations.status.disabled')}
          </StatusPill>
        ) : null}
      </h3>
      <p className="console-station-card__meta">
        <ClassLabel klass={klass} fallback={station.classId} />
      </p>
      <p className="console-subtle">
        {t(`stations.kind.${station.kind}`)} · {zoneName ?? t('stations.noZone')} · {t('stations.classes.seats', { count: station.capacity })}
      </p>
      {station.kind === 'pc' ? (
        <p className="console-station-card__meta">
          <ClientCell client={client} />
        </p>
      ) : null}
      {canManage ? (
        <div className="console-station-card__actions">
          <Button variant="ghost" size="sm" icon={<Icon name="pencil" />} onClick={onEdit} label={t('stations.edit.actionNamed', { number: station.number })}>
            {t('stations.edit.action')}
          </Button>
          <StationMenu station={station} onRemoval={onRemoval} />
        </div>
      ) : null}
    </article>
  );
}

function removalOf(station: Station, kind: RemovalTarget['kind']): RemovalTarget {
  return {
    kind,
    organizationId: station.organizationId,
    venueId: station.venueId,
    stationId: station.id,
    number: station.number,
    name: station.name,
    paired: station.managed !== false && pairingOf(station).status === 'paired',
  };
}

/** The row's overflow menu beside Edit (`stations:manage`): Unpair… while a PC is paired, and Remove station…. */
function StationMenu({ station, onRemoval }: { readonly station: Station; readonly onRemoval: (kind: RemovalTarget['kind']) => void }): ReactNode {
  const { t } = useTranslation('console');
  const paired = station.managed !== false && pairingOf(station).status === 'paired';
  const items: MenuEntry[] = [
    ...(paired ? [{ id: 'unpair', label: t('stations.removal.unpairAction'), onSelect: () => onRemoval('unpair') }] : []),
    { id: 'remove', label: t('stations.removal.removeAction'), tone: 'danger' as const, onSelect: () => onRemoval('remove') },
  ];
  return (
    <Menu
      align="end"
      items={items}
      trigger={<Button variant="ghost" size="sm" icon={<Icon name="more" />} label={t('stations.removal.moreFor', { number: station.number })} />}
    />
  );
}

/**
 * The pairing cell (F-STN-02, spec/07 §5). A station with a device bound shows "Paired"; an unpaired
 * one a member may manage offers "Pair device", which opens the pairing dialog. A member without
 * `stations:manage` sees only the status; the server enforces the same on every request.
 */
function PairingCell({ station, canManage, onPair }: { readonly station: Station; readonly canManage: boolean; readonly onPair: () => void }): ReactNode {
  const { t } = useTranslation('console');
  // A station staff run (ADR-0033) has nothing to pair: it says so, and offers no Pair.
  if (station.managed === false) {
    return (
      <span title={t('stations.staffRun.hint')}>
        <Badge tone="info" dot>
          {t('stations.staffRun.badge')}
        </Badge>
      </span>
    );
  }
  const pairing = pairingOf(station);
  if (pairing.status === 'paired') {
    return (
      <Badge tone="ok" dot>
        {t('stations.pairing.paired')}
      </Badge>
    );
  }
  if (!canManage) {
    return (
      <Badge tone="neutral" dot>
        {t('stations.pairing.notPaired')}
      </Badge>
    );
  }
  return (
    <Button variant="secondary" size="sm" icon={<Icon name="qr" />} onClick={onPair}>
      {t('stations.pairing.pair')}
    </Button>
  );
}

/** Pair a PC to a station: staff read the 8-character code off the PC's screen and approve it here. */
function PairDialog({ station, onClose }: { readonly station: Station | null; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog
      open={station !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      size="sm"
      eyebrow={station === null ? undefined : (station.name ?? t('floor.tile.numbered', { number: station.number }))}
      title={t('stations.pairing.sheetTitle')}
      description={t('stations.pairing.sheetHint')}
    >
      {station === null ? null : <PairingForm organizationId={station.organizationId} venueId={station.venueId} stationId={station.id} stationNumber={station.number} onPaired={onClose} />}
    </Dialog>
  );
}
